import { ReadonlyVec2 } from 'gl-matrix';
import { PlaneView } from '../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { ToolAction } from '../ToolAction';

enum ZoomActionFocus {
    VIEW_CENTER,
    CURSOR,
}

/**
 * {@link ZoomAction} modifies the {@link PanZoom.scale scaling} of the view
 * under the wheel event. The {@link PanZoom} is resolved per event from the
 * view's own environment, so zooming one view never rescales views of
 * another environment.
 */
export class ZoomAction extends ToolAction {
    /**
     * {@link ModifierKeys} that need to be active to use this {@link ZoomAction}.
     */
    private readonly modifierKeys: ModifierKeys;

    /**
     * {@link ZoomActionFocus} enum used to determine the point to which the zoom events should focus.
     */
    private static FocusMode: ZoomActionFocus = ZoomActionFocus.CURSOR;

    /**
     * @param modifierKeys {@link ModifierKeys} that need to be active to use this {@link ZoomAction}. Defaults to {@link ModifierKeys.NONE NONE}.
     */
    public constructor(modifierKeys: ModifierKeys = ModifierKeys.NONE) {
        super();
        this.modifierKeys = modifierKeys;
    }

    /**
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this {@link ZoomAction}.\
     * This updates the {@link PanZoom.scale scaling} of the {@link view}'s environment if the event is successfully handled.
     * @param delta The direction of the MouseWheel event.\
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link TextureView} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if the event was successfully handled.
     * The event is always successfully handled if the {@link modifierKeys} match.\
     * {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} if the {@link modifierKeys} don't match, in which case the event wasn't handled.
     */
    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (modifierKeys.equals(this.modifierKeys)) {
            const panZoom = view.panZoom;
            const focusCoordinates =
                ZoomAction.FocusMode === ZoomActionFocus.CURSOR
                    ? coordinates
                    : undefined;
            if (delta > 0) {
                panZoom.zoomIn(view, focusCoordinates);
            } else {
                panZoom.zoomOut(view, focusCoordinates);
            }

            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
